import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import type {
  CareTeamNote,
  CareTeamNotesResponse,
  NewCareTeamNote,
} from "@/types/careTeamNotes";
import { apiFetch } from "./client";

/** Clinician portal (nurses, prescribers) or the admin support chart. */
export type NotesSurface = "clinician" | "admin";

function base(surface: NotesSurface, patientId: string): string {
  const root = surface === "admin" ? "/admin/clinical-admin" : "/clinician";
  return `${root}/patients/${encodeURIComponent(patientId)}/care-team-notes`;
}

export function useCareTeamNotes(patientId: string, surface: NotesSurface = "clinician") {
  const { session, loading } = useAuth();
  const userId = loading ? null : (session?.user.id ?? null);
  return useQuery({
    queryKey: ["care-team-notes", userId, surface, patientId],
    queryFn: () => apiFetch<CareTeamNotesResponse>(base(surface, patientId)),
    enabled: Boolean(userId && patientId),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

function useInvalidateNotes() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["care-team-notes"] }),
      // A sign-off request is a dose-change hold and a chart history row.
      qc.invalidateQueries({ queryKey: ["dose-change-holds"] }),
      qc.invalidateQueries({ queryKey: ["clinician", "checkin-dose-history"] }),
      qc.invalidateQueries({ queryKey: ["admin", "clinical-admin", "patient"] }),
    ]);
}

export function useCreateCareTeamNote(patientId: string, surface: NotesSurface = "clinician") {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: (note: NewCareTeamNote) =>
      apiFetch<CareTeamNote>(base(surface, patientId), {
        method: "POST",
        body: JSON.stringify(note),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateCareTeamNote(patientId: string, surface: NotesSurface = "clinician") {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: ({
      noteId,
      patch,
    }: {
      noteId: string;
      patch: { pinned?: boolean; resolved?: boolean };
    }) =>
      apiFetch<CareTeamNote>(`${base(surface, patientId)}/${encodeURIComponent(noteId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: invalidate,
  });
}
